import React, { Fragment, useState } from "react";
import { Avatar, Box, Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { apiUrl } from "../../services/api";
import {
    ChatAnexo,
    ChatMensagemType,
    corAvatar,
    horaMensagem,
    iniciaisNome,
    isImagem,
    normalizar,
    segmentosMencao,
    tamanhoLegivel,
} from "./chatTypes";

type Props = {
    mensagem: ChatMensagemType
    propria: boolean
    mostrarAutor: boolean
    podeApagar: boolean
    lidaPorTodos: boolean
    nomesMencionaveis?: string[]   // nomes dos participantes, para destacar @fulano
    meuNome?: string
    idUsuarioLogado?: number
    onApagar: (id: number) => void
    onBaixarAnexo: (anexo: ChatAnexo) => void
};

function ChatMensagem({ mensagem, propria, mostrarAutor, podeApagar, lidaPorTodos, nomesMencionaveis = [], meuNome = "", idUsuarioLogado = 0, onApagar, onBaixarAnexo }: Props) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const apagada = Number(mensagem.chat_mensagem_ativa) === 0;

    // Fui citado? (meu id na coluna mencoes_mensagem) → o balão ganha destaque
    const mencionaVoce = String(mensagem.mencoes_mensagem || "")
        .split(",")
        .some(id => Number(id) === Number(idUsuarioLogado) && Number(idUsuarioLogado) > 0);

    // Mensagem de sistema ("Fulano entrou no grupo") é centralizada, sem balão
    if (mensagem.tipo_mensagem === 'sistema') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Chip
                    size="small"
                    label={mensagem.conteudo_mensagem || ''}
                    sx={{ bgcolor: '#e0e0e0', color: '#555', fontSize: 11 }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: propria ? 'flex-end' : 'flex-start', mb: 0.5 }}>
            {!propria && (
                <Box sx={{ width: 32, mr: 1, flexShrink: 0 }}>
                    {mostrarAutor && (
                        <Avatar
                            src={String(mensagem.photo_usuario || '').length > 0 ? `${apiUrl}/imguploaded/${mensagem.photo_usuario}` : ""}
                            sx={{ width: 28, height: 28, bgcolor: corAvatar(mensagem.id_usuario_remetente), fontSize: 11 }}
                        >
                            {iniciaisNome(mensagem.nome_usuario)}
                        </Avatar>
                    )}
                </Box>
            )}

            <Box
                sx={{
                    maxWidth: '78%',
                    bgcolor: apagada ? 'transparent' : (propria ? '#d9fdd3' : (mencionaVoce ? '#fff8e1' : '#fff')),
                    border: apagada ? '1px dashed #bdbdbd' : '1px solid #e0e0e0',
                    borderLeft: !apagada && mencionaVoce ? '3px solid #f9a825' : undefined,
                    borderRadius: 1.5,
                    px: 1.2,
                    py: 0.7,
                    position: 'relative',
                    '&:hover .chat-acoes': { opacity: 1 },
                }}
            >
                {mostrarAutor && !propria && (
                    <Typography sx={{ fontSize: 11, fontWeight: 'bold', color: corAvatar(mensagem.id_usuario_remetente) }}>
                        {mensagem.nome_usuario}
                    </Typography>
                )}

                {apagada ? (
                    <Typography sx={{ fontSize: 12.5, fontStyle: 'italic', color: '#9e9e9e' }}>
                        Mensagem apagada
                    </Typography>
                ) : (
                    <>
                        {mensagem.anexos && mensagem.anexos.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: mensagem.conteudo_mensagem ? 0.5 : 0 }}>
                                {mensagem.anexos.map(anexo => (
                                    <Box
                                        key={anexo.id_chat_anexo}
                                        onClick={() => onBaixarAnexo(anexo)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 0.8,
                                            borderRadius: 1,
                                            bgcolor: 'rgba(0,0,0,0.04)',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                        }}
                                    >
                                        {isImagem(anexo.mime_type)
                                            ? <DownloadIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                            : <InsertDriveFileIcon sx={{ fontSize: 20, color: '#1565c0' }} />}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {anexo.nome_original}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: '#757575' }}>
                                                {tamanhoLegivel(anexo.tamanho_bytes)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {mensagem.conteudo_mensagem && (
                            <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {segmentosMencao(mensagem.conteudo_mensagem, nomesMencionaveis).map((parte, i) => {
                                    if (!parte.nome) return <Fragment key={i}>{parte.texto}</Fragment>;
                                    const souEu = normalizar(parte.nome) === normalizar(meuNome);
                                    return (
                                        <Box
                                            key={i}
                                            component="span"
                                            sx={{
                                                fontWeight: 600,
                                                color: souEu ? '#e65100' : '#1565c0',
                                                bgcolor: souEu ? 'rgba(230,81,0,0.12)' : 'rgba(21,101,192,0.10)',
                                                borderRadius: 0.5,
                                                px: 0.3,
                                            }}
                                        >
                                            {parte.texto}
                                        </Box>
                                    );
                                })}
                            </Typography>
                        )}
                    </>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.2 }}>
                    <Typography sx={{ fontSize: 9.5, color: '#9e9e9e' }}>
                        {horaMensagem(mensagem.created_at)}
                    </Typography>
                    {propria && !apagada && (
                        <Tooltip title={lidaPorTodos ? "Lida" : "Enviada"}>
                            <DoneAllIcon sx={{ fontSize: 13, color: lidaPorTodos ? '#1565c0' : '#bdbdbd' }} />
                        </Tooltip>
                    )}
                </Box>

                {podeApagar && !apagada && (
                    <>
                        <IconButton
                            className="chat-acoes"
                            size="small"
                            onClick={e => setAnchorEl(e.currentTarget)}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: propria ? 'auto' : -28,
                                left: propria ? -28 : 'auto',
                                opacity: 0,
                                transition: 'opacity .15s',
                                p: 0.3,
                            }}
                        >
                            <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem
                                onClick={() => {
                                    setAnchorEl(null);
                                    onApagar(mensagem.id_chat_mensagem);
                                }}
                                sx={{ fontSize: 13, color: 'error.main' }}
                            >
                                Apagar mensagem
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
        </Box>
    );
}

// memo: numa conversa longa, só re-renderiza o balão que realmente mudou
export default React.memo(ChatMensagem, (ant, prox) => (
    ant.mensagem.id_chat_mensagem === prox.mensagem.id_chat_mensagem &&
    ant.mensagem.chat_mensagem_ativa === prox.mensagem.chat_mensagem_ativa &&
    ant.mostrarAutor === prox.mostrarAutor &&
    ant.lidaPorTodos === prox.lidaPorTodos &&
    ant.podeApagar === prox.podeApagar
));
